import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const SOURCES = ["deal", "user", "listing"];

export default function AuditTab() {
  const [items, setItems] = useState([]);
  const [dealId, setDealId] = useState("");
  const [userId, setUserId] = useState("");
  const [noteId, setNoteId] = useState("");
  const [event, setEvent] = useState("");
  const [source, setSource] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { limit: 500 };
    if (dealId) params.deal_id = dealId;
    if (userId) params.user_id = userId;
    if (noteId) params.note_id = noteId;
    if (event) params.event = event;
    if (source) params.source = source;
    if (from) params.from_date = new Date(from).toISOString();
    if (to)   params.to_date   = new Date(to + "T23:59:59").toISOString();
    api.get("/admin/audit", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // initial

  const exportCsv = () => {
    const p = new URLSearchParams();
    if (dealId) p.set("deal_id", dealId);
    if (userId) p.set("user_id", userId);
    if (noteId) p.set("note_id", noteId);
    if (event) p.set("event", event);
    if (source) p.set("source", source);
    if (from) p.set("from_date", new Date(from).toISOString());
    if (to)   p.set("to_date",   new Date(to + "T23:59:59").toISOString());
    // Directly download using a hidden anchor
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/audit.csv?${p.toString()}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-4" data-testid="admin-audit-tab">
      <div className="border border-border p-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <Input placeholder="Deal ID" value={dealId} onChange={(e) => setDealId(e.target.value)} className="rounded-none" data-testid="audit-filter-deal" />
        <Input placeholder="User ID (actor or target)" value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded-none" data-testid="audit-filter-user" />
        <Input placeholder="Note ID" value={noteId} onChange={(e) => setNoteId(e.target.value)} className="rounded-none" data-testid="audit-filter-note" />
        <Input placeholder="Event type (e.g. status_changed)" value={event} onChange={(e) => setEvent(e.target.value)} className="rounded-none" data-testid="audit-filter-event" />
        <Select value={source} onValueChange={(v) => setSource(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none" data-testid="audit-filter-source"><SelectValue placeholder="Any source" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">Any source</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none" data-testid="audit-filter-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none" data-testid="audit-filter-to" />
        <div className="flex gap-2">
          <Button className="rounded-none uppercase text-xs h-9" onClick={load} data-testid="audit-apply">Apply</Button>
          <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={exportCsv} data-testid="audit-csv-export">CSV</Button>
        </div>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="audit-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-muted/50 text-left">
            <tr>
              <Th>When</Th><Th>Source</Th><Th>Event</Th><Th>Actor</Th><Th>Deal / Note / Target</Th><Th>Details</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No matching events.</td></tr>}
            {items.map((r, i) => (
              <tr key={i} className="border-t border-border align-top" data-testid={`audit-row-${i}`}>
                <Td className="font-mono text-[10px] whitespace-nowrap">{r.at ? new Date(r.at).toLocaleString() : "—"}</Td>
                <Td className="font-mono text-[10px] uppercase">{r.source}</Td>
                <Td className="font-mono text-[10px] uppercase">{r.event}</Td>
                <Td className="font-mono text-[10px]">
                  {r.actor_role || "?"} · {(r.actor_id || "").slice(-6)}
                  {r.actor_email && <div className="text-muted-foreground">{r.actor_email}</div>}
                </Td>
                <Td className="font-mono text-[10px]">
                  {r.deal_id && <div><Link className="text-primary hover:underline" to={`/deals/${r.deal_id}`}>deal {r.deal_id.slice(-8)}</Link></div>}
                  {r.note_id && <div>note {r.note_id.slice(-8)}</div>}
                  {r.target_user_id && <div>user {r.target_user_id.slice(-8)}</div>}
                </Td>
                <Td className="font-mono text-[10px] break-all max-w-md">{JSON.stringify(r.details || {})}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-muted-foreground text-[10px] whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
